import {
  BookOpen,
  Briefcase,
  CalendarDays,
  Car,
  Check,
  Clock,
  ExternalLink,
  FileDown,
  FileText,
  GraduationCap,
  Mail,
  Phone,
  RefreshCw,
  TrendingUp,
  User,
  Wallet,
  X,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import DocumentPreviewModal from '../../common/DocumentPreviewModal';
import FormateurProfilExtrasEditor from '../../formateur/FormateurProfilExtrasEditor';
import type {
  FormateurDiplome,
  FormateurDisponibilite,
  FormateurReferencement,
} from '../../../types/formateurProfilExtras';

function FicheCardShell({
  icon,
  title,
  subtitle,
  badge,
  children,
  variant = 'default',
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  badge?: ReactNode;
  children: ReactNode;
  variant?: 'default' | 'accent' | 'success';
}) {
  return (
    <section className={`formateur-fiche-card formateur-fiche-card--${variant}`}>
      <header className="formateur-fiche-card__header">
        <span className="formateur-fiche-card__icon" aria-hidden>
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="formateur-fiche-card__title">{title}</h3>
          {subtitle ? <p className="formateur-fiche-card__subtitle">{subtitle}</p> : null}
        </div>
        {badge ? <div className="shrink-0">{badge}</div> : null}
      </header>
      <div className="formateur-fiche-card__body">{children}</div>
    </section>
  );
}

function docStatusTone(statut: string): 'valid' | 'rejected' | 'pending' {
  if (statut === 'VALIDE') return 'valid';
  if (statut === 'REJETE') return 'rejected';
  return 'pending';
}

function formatDocType(type: string): string {
  return type.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

type DocumentRow = {
  id: string;
  typeDocument: string;
  statutValidation: string;
  urlFichier: string;
};

export function FormateurDocumentsCard({
  documents,
  onValidate,
  onReject,
}: {
  documents: DocumentRow[];
  onValidate: (id: string) => Promise<void>;
  onReject: (id: string) => Promise<void>;
}) {
  const pending = documents.filter((d) => d.statutValidation === 'EN_ATTENTE').length;
  const [preview, setPreview] = useState<{ title: string; apiPath: string } | null>(null);

  return (
    <FicheCardShell
      variant="accent"
      icon={<FileText className="h-5 w-5" />}
      title="Documents formateur"
      subtitle="Validation administrative"
      badge={
        documents.length > 0 ? (
          <span className="formateur-fiche-badge">
            {pending > 0 ? `${pending} en attente` : `${documents.length} fichier(s)`}
          </span>
        ) : null
      }
    >
      {documents.length === 0 ? (
        <p className="formateur-fiche-empty">Aucun document déposé par le formateur.</p>
      ) : (
        <ul className="formateur-fiche-doc-list">
          {documents.map((doc) => {
            const tone = docStatusTone(doc.statutValidation);
            const canAct = doc.statutValidation === 'EN_ATTENTE';
            return (
              <li key={doc.id} className="formateur-fiche-doc-item">
                <div className="formateur-fiche-doc-item__main">
                  <span className={`formateur-fiche-status formateur-fiche-status--${tone}`}>
                    {doc.statutValidation.replace(/_/g, ' ')}
                  </span>
                  <p className="formateur-fiche-doc-item__type">{formatDocType(doc.typeDocument)}</p>
                  <button
                    type="button"
                    className="formateur-fiche-link"
                    onClick={() =>
                      setPreview({
                        title: formatDocType(doc.typeDocument),
                        apiPath: `/admin/documents/${encodeURIComponent(doc.id)}/fichier`,
                      })
                    }
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    Consulter le fichier
                  </button>
                </div>
                {canAct ? (
                  <div className="formateur-fiche-doc-item__actions">
                    <button
                      type="button"
                      className="formateur-fiche-btn formateur-fiche-btn--success"
                      onClick={() => void onValidate(doc.id)}
                    >
                      <Check className="h-3.5 w-3.5" aria-hidden />
                      Valider
                    </button>
                    <button
                      type="button"
                      className="formateur-fiche-btn formateur-fiche-btn--danger"
                      onClick={() => void onReject(doc.id)}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                      Rejeter
                    </button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <DocumentPreviewModal
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        title={preview?.title ?? 'Document'}
        apiPath={preview?.apiPath ?? null}
      />
    </FicheCardShell>
  );
}

type ProfilProps = {
  prenom?: string;
  nom?: string;
  email?: string;
  telephone?: string;
  photoUrl?: string;
  specialite?: string;
  experienceAnnees?: number;
  biographie?: string;
  urlCv?: string;
  /** Chemin API authentifié pour ouvrir le CV (ex. `/admin/formateurs/:id/cv`) */
  cvApiPath?: string | null;
  diplomes: FormateurDiplome[];
  disponibilites: FormateurDisponibilite[];
  referencements: FormateurReferencement[];
  onValidateReferencement: (refId: string) => Promise<void>;
  onRejectReferencement: (refId: string) => Promise<void>;
};

export function FormateurProfilReadonlyCard({
  prenom,
  nom,
  email,
  telephone,
  photoUrl,
  specialite,
  experienceAnnees,
  biographie,
  urlCv,
  cvApiPath,
  diplomes,
  disponibilites,
  referencements,
  onValidateReferencement,
  onRejectReferencement,
}: ProfilProps) {
  const fullName = [prenom, nom].filter(Boolean).join(' ') || 'Formateur';
  const [cvPreviewOpen, setCvPreviewOpen] = useState(false);
  const canViewCv = Boolean(urlCv && cvApiPath);

  return (
    <FicheCardShell
      icon={<User className="h-5 w-5" />}
      title="Profil formateur"
      subtitle="Informations déclarées"
    >
      <div className="formateur-fiche-profil-hero">
        {photoUrl ? (
          <img src={photoUrl} alt="" className="formateur-fiche-avatar" />
        ) : (
          <span className="formateur-fiche-avatar formateur-fiche-avatar--placeholder">
            <User className="h-8 w-8 text-primary/60" aria-hidden />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="formateur-fiche-profil-name">{fullName}</p>
          <div className="formateur-fiche-profil-contacts">
            <span className="formateur-fiche-contact">
              <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {email?.trim() || 'E-mail non renseigné'}
            </span>
            <span className="formateur-fiche-contact">
              <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {telephone?.trim() || 'Téléphone non renseigné'}
            </span>
          </div>
        </div>
      </div>

      <div className="formateur-fiche-stat-grid">
        <div className="formateur-fiche-stat">
          <p className="formateur-fiche-stat__label">Spécialité</p>
          <p className="formateur-fiche-stat__value">{specialite || '—'}</p>
        </div>
        <div className="formateur-fiche-stat">
          <p className="formateur-fiche-stat__label">Expérience</p>
          <p className="formateur-fiche-stat__value">{experienceAnnees ?? 0} an(s)</p>
        </div>
      </div>

      <div className="formateur-fiche-quote">
        <p className="formateur-fiche-quote__label">Biographie</p>
        <p className="formateur-fiche-quote__text">{biographie?.trim() || 'Non renseignée.'}</p>
      </div>

      {canViewCv ? (
        <button type="button" className="formateur-fiche-cv-btn" onClick={() => setCvPreviewOpen(true)}>
          <FileText className="h-4 w-4" aria-hidden />
          Consulter le CV
          <ExternalLink className="h-3.5 w-3.5 opacity-70" aria-hidden />
        </button>
      ) : null}

      <DocumentPreviewModal
        open={cvPreviewOpen}
        onClose={() => setCvPreviewOpen(false)}
        title="CV formateur"
        subtitle={fullName}
        apiPath={cvApiPath ?? null}
      />

      <div className="formateur-fiche-nested">
        <FormateurProfilExtrasEditor
          diplomes={diplomes}
          disponibilites={disponibilites}
          referencements={referencements}
          readOnly
          onDiplomesChange={() => {}}
          onDisponibilitesChange={() => {}}
          onReferencementsChange={() => {}}
        />
      </div>

      {referencements.length > 0 ? (
        <div className="formateur-fiche-ref-block">
          <p className="formateur-fiche-ref-block__title">Validation des référencements</p>
          <ul className="space-y-2">
            {referencements.map((r) => {
              const ref = r as FormateurReferencement & { id?: string };
              const refId = ref.id ?? '';
              if (!refId) return null;
              const statut = String(ref.statutValidation ?? 'EN_ATTENTE');
              const tone = docStatusTone(statut);
              const canAct = statut === 'EN_ATTENTE';
              return (
                <li key={refId} className="formateur-fiche-ref-row">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">{r.nom}</p>
                    {r.fonction ? (
                      <p className="text-xs text-on-surface-variant">{r.fonction}</p>
                    ) : null}
                    <span className={`formateur-fiche-status formateur-fiche-status--${tone} mt-1.5`}>
                      {statut.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {canAct ? (
                    <div className="flex shrink-0 flex-col gap-1 sm:flex-row">
                      <button
                        type="button"
                        className="formateur-fiche-btn formateur-fiche-btn--success"
                        onClick={() => void onValidateReferencement(refId)}
                      >
                        <Check className="h-3.5 w-3.5" aria-hidden />
                        Valider
                      </button>
                      <button
                        type="button"
                        className="formateur-fiche-btn formateur-fiche-btn--danger"
                        onClick={() => void onRejectReferencement(refId)}
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                        Rejeter
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </FicheCardShell>
  );
}

type CohorteImpact = {
  code: string;
  libelle?: string;
  moyenneCohorteSur20: number | null;
  tauxPresenceMoyenPct: number | null;
  nombreApprenants: number;
};

type TarifActif = {
  referenceUsdHoraire?: number;
  montantHoraireCdf?: number;
  primeTransportJourCdf?: number;
  referenceUsdTransport?: number;
  personnalise?: boolean;
};

export function FormateurProfilSyntheseCard({
  email,
  telephone,
  specialite,
  experienceAnnees,
  biographie,
  urlCv,
  cvApiPath,
  tarif,
  onResendAccess,
  onSyncPrestations,
  onDownloadPdf,
}: {
  email?: string;
  telephone?: string;
  specialite?: string;
  experienceAnnees?: number;
  biographie?: string;
  urlCv?: string;
  cvApiPath?: string | null;
  tarif?: TarifActif;
  onResendAccess: () => void;
  onSyncPrestations: () => void;
  onDownloadPdf: () => void;
}) {
  const [cvPreviewOpen, setCvPreviewOpen] = useState(false);
  const canViewCv = Boolean(urlCv && cvApiPath);
  const horaireUsd = tarif?.referenceUsdHoraire ?? 10;
  const horaireCdf = Number(tarif?.montantHoraireCdf ?? 0);
  const transportUsd = tarif?.referenceUsdTransport ?? 10;
  const transportCdf = Number(tarif?.primeTransportJourCdf ?? 0);
  const tarifPersonnalise = Boolean(tarif?.personnalise);

  return (
    <FicheCardShell
      variant="accent"
      icon={<Briefcase className="h-5 w-5" />}
      title="Profil"
      subtitle="Synthèse professionnelle et actions"
    >
      <div className="formateur-fiche-profil-contacts formateur-fiche-profil-contacts--synthese">
        <span className="formateur-fiche-contact">
          <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {email?.trim() || 'E-mail non renseigné'}
        </span>
        <span className="formateur-fiche-contact">
          <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {telephone?.trim() || 'Téléphone non renseigné'}
        </span>
      </div>

      <div className="formateur-fiche-synthese-meta">
        <div className="formateur-fiche-stat">
          <p className="formateur-fiche-stat__label">Spécialité</p>
          <p className="formateur-fiche-stat__value">{specialite?.trim() || '—'}</p>
        </div>
        <div className="formateur-fiche-stat">
          <p className="formateur-fiche-stat__label">
            <Clock className="mr-1 inline h-3 w-3" aria-hidden />
            Expérience
          </p>
          <p className="formateur-fiche-stat__value">{experienceAnnees ?? 0} an(s)</p>
        </div>
      </div>

      <div className="formateur-fiche-quote">
        <p className="formateur-fiche-quote__label">Biographie</p>
        <p className="formateur-fiche-quote__text">{biographie?.trim() || 'Non renseignée.'}</p>
      </div>

      {canViewCv ? (
        <button type="button" className="formateur-fiche-cv-btn" onClick={() => setCvPreviewOpen(true)}>
          <FileText className="h-4 w-4" aria-hidden />
          Consulter le CV
          <ExternalLink className="h-3.5 w-3.5 opacity-70" aria-hidden />
        </button>
      ) : null}

      <DocumentPreviewModal
        open={cvPreviewOpen}
        onClose={() => setCvPreviewOpen(false)}
        title="CV formateur"
        apiPath={cvApiPath ?? null}
      />

      <div className="formateur-fiche-tarif-panel">
        {tarifPersonnalise ? (
          <p className="mb-2 inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-emerald-800">
            Tarif personnalisé
          </p>
        ) : null}
        <div className="formateur-fiche-tarif-panel__item">
          <span className="formateur-fiche-tarif-panel__icon" aria-hidden>
            <Wallet className="h-4 w-4" />
          </span>
          <div>
            <p className="formateur-fiche-tarif-panel__label">Tarif horaire actif</p>
            <p className="formateur-fiche-tarif-panel__value">
              {horaireUsd} USD/h
              <span className="formateur-fiche-tarif-panel__sep">→</span>
              {horaireCdf.toLocaleString('fr-FR')} CDF
            </p>
          </div>
        </div>
        <div className="formateur-fiche-tarif-panel__item">
          <span className="formateur-fiche-tarif-panel__icon formateur-fiche-tarif-panel__icon--muted" aria-hidden>
            <Car className="h-4 w-4" />
          </span>
          <div>
            <p className="formateur-fiche-tarif-panel__label">Prime transport</p>
            <p className="formateur-fiche-tarif-panel__value">
              {transportUsd} USD / jour
              <span className="formateur-fiche-tarif-panel__sep">→</span>
              {transportCdf.toLocaleString('fr-FR')} CDF
            </p>
          </div>
        </div>
      </div>

      <p className="formateur-fiche-actions-title">Actions administrateur</p>
      <div className="formateur-fiche-actions">
        <button type="button" className="formateur-fiche-action-btn" onClick={onResendAccess}>
          <Mail className="h-4 w-4 shrink-0" aria-hidden />
          <span>Renvoyer accès</span>
        </button>
        <button type="button" className="formateur-fiche-action-btn formateur-fiche-action-btn--primary" onClick={onSyncPrestations}>
          <RefreshCw className="h-4 w-4 shrink-0" aria-hidden />
          <span>Sync prestations</span>
        </button>
        <button type="button" className="formateur-fiche-action-btn" onClick={onDownloadPdf}>
          <FileDown className="h-4 w-4 shrink-0" aria-hidden />
          <span>PDF paiement</span>
        </button>
        <Link to="/admin/planning" className="formateur-fiche-action-btn formateur-fiche-action-btn--ghost">
          <CalendarDays className="h-4 w-4 shrink-0" aria-hidden />
          <span>Planning</span>
        </Link>
      </div>

      <p className="formateur-fiche-tip">
        Les présences enregistrées clôturent la session et créent la prestation automatiquement.
      </p>
    </FicheCardShell>
  );
}

export function FormateurImpactCard({
  cohortes,
  moyenneGlobaleSur20,
}: {
  cohortes: CohorteImpact[];
  moyenneGlobaleSur20: number | null;
}) {
  return (
    <FicheCardShell
      variant="success"
      icon={<TrendingUp className="h-5 w-5" />}
      title="Impact académique"
      subtitle="Cohortes animées"
      badge={
        <span className="formateur-fiche-badge formateur-fiche-badge--muted">
          {cohortes.length} cohorte{cohortes.length > 1 ? 's' : ''}
        </span>
      }
    >
      {moyenneGlobaleSur20 != null ? (
        <div className="formateur-fiche-impact-summary">
          <GraduationCap className="h-6 w-6 text-primary" aria-hidden />
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-on-surface-variant">
              Moyenne globale
            </p>
            <p className="text-2xl font-black text-primary">{moyenneGlobaleSur20}/20</p>
          </div>
        </div>
      ) : null}

      <ul className="formateur-fiche-cohort-grid">
        {cohortes.map((c) => (
          <li key={c.code} className="formateur-fiche-cohort-card">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-bold text-slate-900">{c.code}</p>
                {c.libelle ? (
                  <p className="mt-0.5 truncate text-xs text-on-surface-variant">{c.libelle}</p>
                ) : null}
              </div>
              <span className="formateur-fiche-cohort-pill">
                <BookOpen className="h-3 w-3" aria-hidden />
                {c.nombreApprenants}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-end justify-between gap-2">
              <div>
                <p className="text-[10px] font-bold uppercase text-on-surface-variant">Moyenne</p>
                <p className="text-lg font-black text-slate-900">
                  {c.moyenneCohorteSur20 != null ? `${c.moyenneCohorteSur20}/20` : '—'}
                </p>
              </div>
              {c.tauxPresenceMoyenPct != null ? (
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase text-on-surface-variant">Présence</p>
                  <p className="text-sm font-bold text-emerald-700">{c.tauxPresenceMoyenPct}%</p>
                </div>
              ) : null}
            </div>
            {c.tauxPresenceMoyenPct != null ? (
              <div className="formateur-fiche-progress" aria-hidden>
                <span
                  className="formateur-fiche-progress__bar"
                  style={{ width: `${Math.min(100, Math.max(0, c.tauxPresenceMoyenPct))}%` }}
                />
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </FicheCardShell>
  );
}
